/**
 * 거래처 상세 — 거래 가능/불가 약품 선택 모달
 * 레거시 `EdtTradableAndNonTradableDrugs.js` 포팅 (+ 의약품명/코드 검색)
 * 확정 시 태그는 `의약품코드 | 의약품명` 형태로 부모에 전달한다.
 */
import { drugService, type DrugInfoResponse } from '@/entities/drug/api/drug.api';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { FilterRow } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { cn } from '@/shared/lib/utils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

/** API/기존 태그에서 순수 의약품 코드만 추출 (`코드 | 명` 또는 코드만) */
export const extractDrugCode = (entry: string): string => {
  const trimmed = entry.trim();
  const sep = ' | ';
  const idx = trimmed.indexOf(sep);
  return idx >= 0 ? trimmed.slice(0, idx).trim() : trimmed;
};

export const formatDrugCodeLabel = (
  drugCode: string,
  drugName: string,
): string => `${drugCode} | ${drugName}`;

interface ClientDrugSelectModalProps {
  open: boolean;
  pharmaCompanyName: string;
  refYm: string;
  existingDrugCodes: string[];
  onClose: () => void;
  /** `의약품코드 | 의약품명` 문자열 배열 */
  onConfirm: (drugLabels: string[]) => void;
}

export const ClientDrugSelectModal: React.FC<ClientDrugSelectModalProps> = ({
  open,
  pharmaCompanyName,
  refYm,
  existingDrugCodes,
  onClose,
  onConfirm,
}) => {
  const toast = useToast();
  const [keyword, setKeyword] = useState('');
  /** 실제 조회에 쓰는 검색어 — 검색 버튼/엔터 시에만 반영 */
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [searchNonce, setSearchNonce] = useState(0);
  const [loading, setLoading] = useState(false);
  const [drugs, setDrugs] = useState<DrugInfoResponse[]>([]);
  /** 검색 전환 후에도 이름 유지를 위해 코드→메타 보관 */
  const [selected, setSelected] = useState<
    Record<string, { drugCode: string; drugName: string }>
  >({});

  const existingSet = useMemo(
    () => new Set(existingDrugCodes.map(extractDrugCode).filter(Boolean)),
    [existingDrugCodes],
  );

  useEffect(() => {
    if (!open) {
      setKeyword('');
      setAppliedKeyword('');
      setSearchNonce(0);
      setDrugs([]);
      setSelected({});
      return;
    }

    let cancelled = false;
    const fetchDrugs = async () => {
      setLoading(true);
      try {
        const kw = appliedKeyword.trim();
        const isNumeric = /^\d+$/.test(kw);
        const response = await drugService.getDrugs(
          {
            pharmaCompanyName,
            refYm,
            ...(kw
              ? isNumeric
                ? { drugCode: kw }
                : { drugName: kw }
              : {}),
          },
          { page: 0, size: 500 },
        );
        if (cancelled) return;
        setDrugs(response.content ?? []);
      } catch (e) {
        if (cancelled) return;
        handleApiError(e, toast);
        setDrugs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchDrugs();
    return () => {
      cancelled = true;
    };
    // toast는 매 렌더 새 객체라 deps 제외 (무한 fetch·깜빡임 방지)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchNonce로 동일 조건 재검색
  }, [open, pharmaCompanyName, refYm, appliedKeyword, searchNonce]);

  const handleSearch = () => {
    setAppliedKeyword(keyword.trim());
    setSearchNonce((n) => n + 1);
  };

  const toggleCode = (drug: DrugInfoResponse, checked: boolean) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (checked) {
        next[drug.drugCode] = {
          drugCode: drug.drugCode,
          drugName: drug.drugName,
        };
      } else {
        delete next[drug.drugCode];
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(
      Object.values(selected).map((item) =>
        formatDrugCodeLabel(item.drugCode, item.drugName),
      ),
    );
    onClose();
  };

  const selectedCount = Object.keys(selected).length;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col gap-4 overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>{pharmaCompanyName} 의약품 목록</DialogTitle>
        </DialogHeader>

        <FilterRow
          gap={12}
          className="w-full shrink-0"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <div className="min-w-0 flex-1 space-y-1">
            <Label htmlFor="client-drug-search">검색 (의약품명 또는 코드)</Label>
            <Input
              id="client-drug-search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="w-full"
              placeholder="의약품명 또는 코드"
            />
          </div>
          <Button
            type="submit"
            className="ruda-button shrink-0 self-end"
            disabled={loading}
          >
            <Search className="mr-2 h-4 w-4" />
            검색
          </Button>
        </FilterRow>

        <div className="relative min-h-0 flex-1 overflow-auto rounded-lg border">
          {loading && drugs.length === 0 ? (
            <div className="flex min-h-[200px] items-center justify-center py-12">
              <LoadingSpinner tip="의약품 목록을 불러오는 중..." />
            </div>
          ) : drugs.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              조회된 의약품이 없습니다.
            </p>
          ) : (
            <div
              className={cn(
                'transition-opacity',
                loading && 'pointer-events-none opacity-50',
              )}
            >
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-white">
                  <TableRow>
                    <TableHead className="w-10" />
                    <TableHead>년월</TableHead>
                    <TableHead>의약품 코드</TableHead>
                    <TableHead>의약품 MFDS 코드</TableHead>
                    <TableHead>제약사 의약품 코드</TableHead>
                    <TableHead>의약품명</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drugs.map((drug) => {
                    const disabled = existingSet.has(drug.drugCode);
                    const checked = Boolean(selected[drug.drugCode]);
                    return (
                      <TableRow key={drug.drugCode}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={(e) =>
                              toggleCode(drug, e.target.checked)
                            }
                            aria-label={`${drug.drugName} 선택`}
                          />
                        </TableCell>
                        <TableCell>{drug.refYm}</TableCell>
                        <TableCell>{drug.drugCode}</TableCell>
                        <TableCell>{drug.drugCodeMfds}</TableCell>
                        <TableCell>{drug.drugCodePharma}</TableCell>
                        <TableCell>{drug.drugName}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button type="button" variant="outline" onClick={onClose}>
            취소
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={selectedCount === 0}
          >
            선택 목록 추가
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
